import { type Expect } from '@type-challenges/utils';
import { cx } from 'cva';
import { type FunctionComponent, useRef, useState } from 'react';
import { type PickDeep } from 'type-fest';
import { Link } from '../../../atoms/Link/Link';
import { RouterLink } from '../../../atoms/RouterLink/RouterLink';
import { MEMBER_CONTACT_US_URL } from '../../../constants';
import {
  type RootState,
  type StrictSubsetOfRootState,
} from '../../../core/root.state';
import { useSelector } from '../../../hooks/useSelector';
import { useTranslation } from '../../../i18n/resources';
import { CloseIcon } from '../../../icons/CloseIcon';
import { MenuIcon } from '../../../icons/Menu';
import { AppRoute } from '../../../routes';
import { MediShareLogo } from '../../MediShareLogo/MediShareLogo';
import styles from './MobileMenu.module.css';

export type MobileMenuState = PickDeep<RootState, 'auth.loggedIn'>;

type CheckState = Expect<StrictSubsetOfRootState<MobileMenuState>>;

export type MobileMenuProps = {
  className?: string;
  failSafe?: boolean;
};

export const MobileMenu: FunctionComponent<MobileMenuProps> = ({
  className,
  failSafe = false,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { loggedIn } = useSelector((state: MobileMenuState) => state.auth);

  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const handleToggleMenu = (): void => {
    setMenuOpen(!menuOpen);
  };

  return (
    <div className={cx(styles.root, className)} ref={rootRef}>
      <div className={cx(styles.topBar, menuOpen && styles.topBarOpen)}>
        <div className={styles.logo}>
          {!failSafe && (
            <RouterLink to={AppRoute['/chat']}>
              <MediShareLogo />
            </RouterLink>
          )}
          {failSafe && (
            <Link href={AppRoute['/chat']}>
              <MediShareLogo />
            </Link>
          )}
        </div>
        {!failSafe && loggedIn && (
          <button
            className={cx(styles.unstyledButton, styles.menuButton)}
            onClick={handleToggleMenu}
            type="button"
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        )}
        {failSafe && loggedIn && (
          <a className={styles.topLevelLink} href={AppRoute['/logout']}>
            {t('common', 'log_out')}
          </a>
        )}
        {failSafe && !loggedIn && (
          <a className={styles.topLevelLink} href={MEMBER_CONTACT_US_URL}>
            {t('common', 'contact')}
          </a>
        )}
      </div>

      {menuOpen && (
        <div className={styles.listTiles}>
          <a
            className={cx(styles.topLevelLink, styles.listTile)}
            href={MEMBER_CONTACT_US_URL}
          >
            {t('common', 'contact')}
          </a>

          <a
            className={cx(styles.topLevelLink, styles.listTile)}
            href={AppRoute['/logout']}
          >
            {t('common', 'log_out')}
          </a>
        </div>
      )}
    </div>
  );
};
